// Transit-aware flow (Phase 1, pure + tested; not yet wired into any screen).
//
// Today the network-flow model attributes H2H/Direct volume to the destination on
// the SAME day it ships. These helpers let a caller instead land it on
// `ship date + transit days`, so volume shipped from a far hub arrives later.
//
// Pure by design: callers inject `own` (origin ship-date volume), `share`
// (origin→branch fraction) and `transit` (days) so this file has no engine/DB deps
// beyond geo + date math and is fully unit-testable.

import { haversine, transitDays } from "../geo";
import { addDays } from "../format";

export interface GeoPoint {
  lat?: number | null;
  lng?: number | null;
}

/**
 * Transit days for volume from an origin hub to a destination branch.
 * - Local (origin IS the branch's home hub) → 0 (same-metro, same-day).
 * - A Direct lane override (`directDays`) wins when present.
 * - Otherwise haversine(origin → branch) bucketed to 1–5 days.
 * - Missing coordinates → 0 (fall back to same-day rather than guess).
 */
export function transitDaysFor(
  origin: GeoPoint,
  dest: GeoPoint,
  opts: { local?: boolean; directDays?: number | null } = {}
): number {
  if (opts.local) return 0;
  if (opts.directDays != null) return opts.directDays;
  if (origin.lat == null || origin.lng == null || dest.lat == null || dest.lng == null) return 0;
  return transitDays(haversine(origin.lat, origin.lng, dest.lat, dest.lng));
}

/**
 * Per-hub, per-day H2H/Direct volume RECEIVED IN from OTHER hubs (arrival basis,
 * shifted by transit). Returns a map `${hubId}|${date}` → received volume. A hub's
 * SORTATION load is its own(hub,date) + this received-in (everything it handles).
 */
export function receivedInByDay(args: {
  hubIds: string[];
  branches: Array<{ id: string; hubId: string }>;
  dates: string[];
  own: (hubId: string, date: string) => number;
  share: (originHubId: string, branchId: string) => number;
  transit: (originHubId: string, branchId: string) => number;
}): Map<string, number> {
  const { hubIds, branches, dates, own, share, transit } = args;
  const branchesByHub = new Map<string, Array<{ id: string; hubId: string }>>();
  for (const b of branches) {
    const arr = branchesByHub.get(b.hubId) ?? [];
    arr.push(b);
    branchesByHub.set(b.hubId, arr);
  }
  const out = new Map<string, number>();
  for (const H of hubIds) {
    const mine = branchesByHub.get(H) ?? [];
    if (mine.length === 0) continue;
    for (const d of dates) {
      let v = 0;
      for (const o of hubIds) {
        if (o === H) continue;
        for (const b of mine) {
          const s = share(o, b.id);
          if (s <= 0) continue;
          v += own(o, addDays(d, -transit(o, b.id))) * s;
        }
      }
      if (v !== 0) out.set(`${H}|${d}`, v);
    }
  }
  return out;
}

/**
 * Build a `(originHubId, branchId) → transit days` function matching Branch
 * Forecast's logic: Local (origin is the branch's home hub) = home-hub→branch;
 * H2H = origin→home-hub. Returns 0 for everything when `enabled` is false. Cached.
 * (Direct-lane overrides are handled at the per-row level on Branch Forecast; the
 * hub-level aggregations approximate Direct as H2H, which is exact when there are
 * no direct lanes.)
 */
export function makeTransitFn(
  hubs: Array<{ id: string; lat?: number | null; lng?: number | null }>,
  branches: Array<{ id: string; hubId: string; lat?: number | null; lng?: number | null }>,
  enabled: boolean
): (originHubId: string, branchId: string) => number {
  if (!enabled) return () => 0;
  const hubById = new Map(hubs.map((h) => [h.id, h]));
  const brById = new Map(branches.map((b) => [b.id, b]));
  const cache = new Map<string, number>();
  return (o: string, bId: string) => {
    const k = `${o}|${bId}`;
    const hit = cache.get(k);
    if (hit !== undefined) return hit;
    const b = brById.get(bId);
    let t = 0;
    if (b) {
      const home = hubById.get(b.hubId);
      t =
        o === b.hubId
          ? transitDaysFor({ lat: home?.lat, lng: home?.lng }, { lat: b.lat, lng: b.lng }) // Local: home → branch
          : transitDaysFor({ lat: hubById.get(o)?.lat, lng: hubById.get(o)?.lng }, { lat: home?.lat, lng: home?.lng }); // H2H: origin → home hub
    }
    cache.set(k, t);
    return t;
  };
}

/**
 * Arrival-basis flow: each origin hub's ship-date volume for a branch lands on
 * `date + transit(origin → branch)`. Returns a map `${branchId}|${date}` → volume
 * that ARRIVES on that date. To populate early dates correctly, pass `own` that can
 * look back up to the max transit before the first date.
 */
export function flowArrivals(args: {
  branches: Array<{ id: string; hubId: string }>;
  hubIds: string[];
  dates: string[];
  own: (hubId: string, date: string) => number; // origin own volume on its SHIP date
  share: (originHubId: string, branchId: string) => number; // 0..1
  transit: (originHubId: string, branchId: string) => number; // days
}): Map<string, number> {
  const { branches, hubIds, dates, own, share, transit } = args;
  const out = new Map<string, number>();
  for (const b of branches) {
    for (const d of dates) {
      let v = 0;
      for (const o of hubIds) {
        const s = share(o, b.id);
        if (s <= 0) continue;
        const shipDate = addDays(d, -transit(o, b.id));
        v += own(o, shipDate) * s;
      }
      if (v !== 0) out.set(`${b.id}|${d}`, v);
    }
  }
  return out;
}
